import React, { useMemo } from "react";
import { Box } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as rentalActions from "src/actions/rentalActions";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import RentalInsuranceDialog from "src/components/RentalInsuranceDialog/RentalInsuranceDialog";
import RentalManageBookingForm from "src/components/RentalManageBookingForm/RentalManageBookingForm";
import getCountryFromURL from "src/utils/getCountryFromUrl";

function getMinDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getMaxDate(currentReturnDate) {
  if (!currentReturnDate) {
    return "";
  }
  const maxDateObj = new Date(currentReturnDate);
  maxDateObj.setDate(maxDateObj.getDate() + 3);
  const year = maxDateObj.getFullYear();
  const month = String(maxDateObj.getMonth() + 1).padStart(2, "0");
  const day = String(maxDateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDateToDDMMYYYY(dateStr) {
  if (!dateStr) {
    return "";
  }
  const [year, month, day] = dateStr.split("-");
  return `${day}-${month}-${year}`;
}

const RentalManageBooking = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({ defaultValues: { newReturnDate: "" } });

  const navigate = useNavigate();
  const ra_number = useSelector((state) => state.rental.rentalVehicle?.LastRANumber);
  const originalReturnDate = useSelector((state) => state.rental.originalReturnDate);
  const outLocation = useSelector((state) => state.rental.outLocation);
  const locationName = useSelector((state) => state.rental.locationName);
  const updateSuccess = useSelector((state) => state.rental.dateUpdateSuccess);
  const raType = useSelector((state) => state.rental.rentalType);
  const loading = useSelector((state) => state.rental.loading);
  const [insuranceDialogOpen, setInsuranceDialogOpen] = React.useState(false);
  const [customerPay, setCustomerPay] = React.useState(false);
  const brand = getBrandFromUrl();
  const country = getCountryFromURL().toLowerCase();
  const dispatch = useDispatch();

  const minDate = useMemo(() => getMinDate(), []);
  const maxDate = useMemo(() => getMaxDate(originalReturnDate), [originalReturnDate]);

  React.useEffect(() => {
    if (raType === "IN") {
      setInsuranceDialogOpen(true);
    }
  }, []);

  React.useEffect(() => {
    if (updateSuccess) {
      navigate(`/rental/manage/confirm?brand=${brand}&country=${country}`, { replace: true });
    }
  }, [updateSuccess]);

  const onSubmit = handleSubmit((data) => {
    const originalTime = originalReturnDate?.split("T")[1];
    const newReturnDate = `${data.newReturnDate}T${originalTime}`;
    dispatch(
      rentalActions.updateReturnDate({
        ra_number,
        DateTime: newReturnDate,
        Location: outLocation,
        Brand: brand.toLowerCase(),
        CustomerPay: customerPay,
        Country: country.toUpperCase(),
      })
    );
  });

  return (
    <Box
      sx={{
        width: "90%",
        margin: "0 auto",
        maxWidth: "800px",
        textAlign: "center",
        padding: "20px",
      }}
    >
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#d9d9d9",
          padding: 5,
          marginTop: "30px",
          borderRadius: "3px",
        }}
      >
        <h1>Your Booking Details</h1>
        <p>Rental Agreement No: {ra_number ?? ""}</p>
        <p>
          Return Date:
          {originalReturnDate ? formatDateToDDMMYYYY(originalReturnDate.split("T")[0]) : ""}
        </p>
        <p>Return Time: {originalReturnDate?.split("T")[1] ?? ""}</p>
        <p>Return Location: {locationName ?? ""}</p>
        <br />
        <p style={{ fontStyle: "italic" }}>
          Please choose your new return date below. You can extend once per booking, for up to 3
          days.
        </p>
      </Box>
      <Box
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          borderRadius: "10px",
          boxShadow: "0 2px 12px 0 rgba(0,0,0,0.10)",
          padding: "10px",
          marginTop: "5px",
          textAlign: "center",
          display: "flex",
        }}
      >
        <Box sx={{ width: "90%", margin: "0 auto" }}>
          <RentalManageBookingForm
            minDate={minDate}
            maxDate={maxDate}
            loading={loading}
            onSubmit={onSubmit}
            errors={errors}
            register={register}
            setValue={setValue}
          />
        </Box>
      </Box>
      <RentalInsuranceDialog
        open={insuranceDialogOpen}
        setCustomerPay={setCustomerPay}
        onClose={() => setInsuranceDialogOpen(false)}
      />
    </Box>
  );
};

export default RentalManageBooking;
